<template>
  <div class="dashboard-page">
    <div ref="stickyHeader" class="dashboard-sticky-header">
    <div class="dashboard-toolbar">
      <nav class="section-nav">
        <a
          v-for="section in sectionLinks"
          :key="section.id"
          :href="'#' + section.id"
          :class="{ 'is-active': activeSection === section.id }"
          @click.prevent="scrollToSection(section.id)"
        >
          {{ section.label }}
        </a>
      </nav>
      <button
        type="button"
        class="btn-filters"
        :class="{ 'is-open': showGlobalFilters }"
        @click="toggleGlobalFilters"
      >
        <img src="@/assets/filter.svg" alt="" class="filter-icon" />
        Filters
        <span v-if="globalFilterCount" class="filter-count">{{ globalFilterCount }}</span>
      </button>
    </div>
    <div v-show="showGlobalFilters" class="global-filters">
      <div class="filter-field">
        <label class="filter-label" for="dashboard-global-time-period">Time Period</label>
        <Select
          id="dashboard-global-time-period"
          v-model="globalFilters.timePeriod"
          :options="yearSeries"
          optionLabel="value"
          optionValue="id"
          placeholder="Time Period"
          class="dashboard-select"
          showClear
        />
      </div>
      <div class="filter-field">
        <label class="filter-label" for="dashboard-global-locations">Locations</label>
        <MultiSelect
          inputId="dashboard-global-locations"
          v-model="globalFilters.locations"
          :options="locations"
          optionLabel="label"
          dataKey="value"
          :filter="true"
          :maxSelectedLabels="1"
          selectedItemsLabel="{0} selected"
          placeholder="All Locations"
          class="dashboard-filter custom-multiselect userSelect"
        >
          <template #empty>
            <span class="text-xs">No Locations Found</span>
          </template>
        </MultiSelect>
      </div>
      <div class="filter-field">
        <label class="filter-label" for="dashboard-global-teams">Teams</label>
        <MultiSelect
          inputId="dashboard-global-teams"
          v-model="globalFilters.departments"
          :options="departmentsList"
          optionLabel="label"
          dataKey="value"
          :filter="true"
          :maxSelectedLabels="1"
          selectedItemsLabel="{0} selected"
          placeholder="All Teams"
          class="dashboard-filter custom-multiselect userSelect"
        >
          <template #empty>
            <span class="text-xs">No Teams Found</span>
          </template>
        </MultiSelect>
      </div>
      <div class="filter-field filter-field-actions">
        <button type="button" class="btn-apply" @click="applyGlobalFilters">Apply</button>
        <button type="button" class="btn-reset" @click="resetGlobalFilters">Reset</button>
        <button type="button" class="btn-reset" @click="cancelGlobalFilters">Cancel</button>
      </div>
    </div>
    <DashboardFilterChips :chips="globalChips" @remove="removeGlobalFilter" />

    <div class="kpi-grid">
      <router-link
        v-for="kpi in kpiCards"
        :key="kpi.key"
        class="kpi-card text-left"
        :class="'kpi-card--' + kpi.tone"
        :to="kpiRoute(kpi.key)"
        @click="markKpiRoute(kpi.key)"
      >
        <div class="kpi-label">{{ kpi.label }}</div>
        <div class="kpi-value">{{ kpi.display }}</div>
      </router-link>
    </div>
    </div>

    <div class="dashboard-body">
    <DashboardSection
      section-id="compliance-review"
      title="Control Review"
      :loading="crLoading"
      :chips="crChips"
      :filter-count="crFilterCount"
      @open="openCrFilters"
      @apply="applyCrFilters"
      @reset="resetCrFilters"
      @cancel="cancelCrFilters"
      @remove="removeCrFilter"
    >
      <template #filters>
        <div class="filter-field">
          <label class="filter-label" for="cr-filter-status">Status</label>
          <MultiSelect
            inputId="cr-filter-status"
            v-model="crFilters.taskStatus"
            :options="statusCodes"
            optionLabel="value"
            dataKey="id"
            :filter="true"
            :maxSelectedLabels="1"
            selectedItemsLabel="{0} selected"
            placeholder="All Statuses"
            class="dashboard-filter custom-multiselect userSelect"
          />
        </div>
        <div class="filter-field">
          <label class="filter-label" for="cr-filter-task-type">Task Type</label>
          <MultiSelect
            inputId="cr-filter-task-type"
            v-model="crFilters.taskType"
            :options="taskType"
            optionLabel="value"
            dataKey="id"
            :filter="true"
            :maxSelectedLabels="1"
            selectedItemsLabel="{0} selected"
            placeholder="Task Type"
            class="dashboard-filter custom-multiselect userSelect"
          />
        </div>
        <div class="filter-field">
          <label class="filter-label" for="cr-filter-cid">CID</label>
          <Select
            inputId="cr-filter-cid"
            v-model="crFilters.control"
            :options="controlsList"
            optionLabel="label"
            optionValue="value"
            placeholder="Select CID"
            class="dashboard-select"
            showClear
          />
        </div>
        <div class="filter-field">
          <label class="filter-label" for="cr-filter-priority">Priority</label>
          <MultiSelect
            inputId="cr-filter-priority"
            v-model="crFilters.controlPriority"
            :options="dbPriority"
            optionLabel="key"
            dataKey="value"
            :filter="true"
            :maxSelectedLabels="1"
            selectedItemsLabel="{0} selected"
            placeholder="All Priorities"
            class="dashboard-filter custom-multiselect userSelect"
          />
        </div>
        <div class="filter-field">
          <label class="filter-label" for="cr-filter-frequency">Frequency</label>
          <MultiSelect
            inputId="cr-filter-frequency"
            v-model="crFilters.controlFrequency"
            :options="dbFrequency"
            optionLabel="key"
            dataKey="value"
            :filter="true"
            :maxSelectedLabels="1"
            selectedItemsLabel="{0} selected"
            placeholder="All Frequencies"
            class="dashboard-filter custom-multiselect userSelect"
          />
        </div>
        <div class="filter-field">
          <label class="filter-label" for="cr-filter-framework">Framework</label>
          <MultiSelect
            inputId="cr-filter-framework"
            v-model="crFilters.framework"
            :options="optionsForFrameworks"
            optionLabel="label"
            :filter="true"
            :maxSelectedLabels="1"
            selectedItemsLabel="{0} selected"
            placeholder="All Frameworks"
            class="dashboard-filter custom-multiselect userSelect"
          />
        </div>
        <div class="filter-field">
          <label class="filter-label" for="cr-filter-tier">Tier</label>
          <Select
            inputId="cr-filter-tier"
            v-model="crFilters.tier"
            :options="tierTypes"
            optionLabel="value"
            optionValue="id"
            placeholder="Tier"
            class="dashboard-select"
            showClear
            @update:modelValue="onCategoryChange"
          />
        </div>
        <div v-if="crFilters.tier == 10107" class="filter-field">
          <label class="filter-label" for="cr-filter-activity">Activities</label>
          <MultiSelect
            inputId="cr-filter-activity"
            v-model="crFilters.activity"
            :options="optionsActivityArray"
            optionLabel="label"
            optionValue="value"
            :filter="true"
            :maxSelectedLabels="1"
            selectedItemsLabel="{0} selected"
            placeholder="Select Activities"
            class="dashboard-filter custom-multiselect userSelect"
          />
        </div>
        <div v-if="crFilters.tier == 10108" class="filter-field">
          <label class="filter-label" for="cr-filter-system">Systems</label>
          <MultiSelect
            inputId="cr-filter-system"
            v-model="crFilters.system"
            :options="optionSystemArray"
            optionLabel="label"
            optionValue="value"
            :filter="true"
            :maxSelectedLabels="1"
            selectedItemsLabel="{0} selected"
            placeholder="Select Systems"
            class="dashboard-filter custom-multiselect userSelect"
          />
        </div>
        <div v-if="crFilters.tier == 10109" class="filter-field">
          <label class="filter-label" for="cr-filter-project">Projects</label>
          <MultiSelect
            inputId="cr-filter-project"
            v-model="crFilters.project"
            :options="optionsProjectArray"
            optionLabel="label"
            optionValue="value"
            :filter="true"
            :maxSelectedLabels="1"
            selectedItemsLabel="{0} selected"
            placeholder="Select Projects"
            class="dashboard-filter custom-multiselect userSelect"
          />
        </div>
      </template>
      <ComplianceReviewSection
        :cr-tasks="crTasks"
        :team-tasks="teamTasks"
        :overdue-aging="overdueAging"
      />
    </DashboardSection>

    <DashboardSection
      section-id="kpis"
      title="KPI Results"
      :loading="kpiLoading"
      :chips="kpiChips"
      :filter-count="kpiFilterCount"
      @open="openKpiFilters"
      @apply="applyKpiFilters"
      @reset="resetKpiFilters"
      @cancel="cancelKpiFilters"
      @remove="removeKpiFilter"
    >
      <template #filters>
        <div class="filter-field">
          <label class="filter-label" for="kpi-filter-id">KPI</label>
          <Select
            inputId="kpi-filter-id"
            v-model="kpiFilters.kpiId"
            :options="optionsKpiArray"
            optionLabel="label"
            optionValue="value"
            placeholder="Select KPI"
            class="dashboard-select"
            showClear
          />
        </div>
        <div class="filter-field">
          <label class="filter-label" for="kpi-filter-time">Time Period</label>
          <Select
            inputId="kpi-filter-time"
            v-model="kpiFilters.timePeriod"
            :options="kpiYearSeries"
            optionLabel="value"
            optionValue="id"
            placeholder="Use page time period"
            class="dashboard-select"
            showClear
          />
        </div>
        <div class="filter-field">
          <label class="filter-label" for="kpi-filter-teams">Teams</label>
          <MultiSelect
            inputId="kpi-filter-teams"
            v-model="kpiFilters.teams"
            :options="departmentsList"
            optionLabel="label"
            dataKey="value"
            :filter="true"
            :maxSelectedLabels="1"
            selectedItemsLabel="{0} selected"
            placeholder="All Teams"
            class="dashboard-filter custom-multiselect userSelect"
          />
        </div>
      </template>
      <KpisSection
        :kpi-results="kpiResults"
        :kpi-results-by-team="kpiResultsByTeam"
        :kpi-results-by-location="kpiResultsByLocation"
      />
    </DashboardSection>

    <DashboardSection
      section-id="controls"
      title="Control Health"
      :loading="controlLoading"
      :chips="controlChips"
      :filter-count="controlFilterCount"
      @open="openControlFilters"
      @apply="applyControlFilters"
      @reset="resetControlFilters"
      @cancel="cancelControlFilters"
      @remove="removeControlFilter"
    >
      <template #filters>
        <div class="filter-field">
          <label class="filter-label" for="control-filter-cid">CID</label>
          <Select
            inputId="control-filter-cid"
            v-model="controlFilters.control"
            :options="controlsList"
            optionLabel="label"
            optionValue="value"
            placeholder="Select CID"
            class="dashboard-select"
            showClear
          />
        </div>
        <div class="filter-field">
          <label class="filter-label" for="control-filter-time">Time Period</label>
          <Select
            inputId="control-filter-time"
            v-model="controlFilters.timePeriod"
            :options="kpiYearSeries"
            optionLabel="value"
            optionValue="id"
            placeholder="Use page time period"
            class="dashboard-select"
            showClear
          />
        </div>
        <div class="filter-field">
          <label class="filter-label" for="control-filter-teams">Teams</label>
          <MultiSelect
            inputId="control-filter-teams"
            v-model="controlFilters.teams"
            :options="departmentsList"
            optionLabel="label"
            dataKey="value"
            :filter="true"
            :maxSelectedLabels="1"
            selectedItemsLabel="{0} selected"
            placeholder="All Teams"
            class="dashboard-filter custom-multiselect userSelect"
          />
        </div>
      </template>
      <ControlsSection
        :control-teams="controlTeams"
      />
    </DashboardSection>

    <DashboardSection
      section-id="findings"
      title="Findings & CAPAs"
      :loading="findingsLoading"
      :chips="findingsChips"
      :filter-count="findingsFilterCount"
      @open="openFindingsFilters"
      @apply="applyFindingsFilters"
      @reset="resetFindingsFilters"
      @cancel="cancelFindingsFilters"
      @remove="removeFindingsFilter"
    >
      <template #filters>
        <div class="filter-field">
          <label class="filter-label" for="findings-filter-ids">Findings</label>
          <MultiSelect
            inputId="findings-filter-ids"
            v-model="findingsFilters.findings"
            :options="optionsForFindingIds"
            optionLabel="label"
            dataKey="value"
            :filter="true"
            :maxSelectedLabels="1"
            selectedItemsLabel="{0} selected"
            placeholder="All Findings"
            class="dashboard-filter custom-multiselect userSelect"
          />
        </div>
        <div class="filter-field">
          <label class="filter-label" for="findings-filter-capa">CAPA Status</label>
          <MultiSelect
            inputId="findings-filter-capa"
            v-model="findingsFilters.taskStatus"
            :options="statusCodes"
            optionLabel="value"
            dataKey="id"
            :filter="true"
            :maxSelectedLabels="1"
            selectedItemsLabel="{0} selected"
            placeholder="CAPA Status"
            class="dashboard-filter custom-multiselect userSelect"
          />
        </div>
      </template>
      <FindingsSection
        :findings="findingChartData"
        :team-findings="teamFindings"
        :capa-tasks="capaTasks"
        :capa-team-tasks="capaTeamTasks"
        :capa-overdue-by-team="capaOverdueByTeam"
      />
    </DashboardSection>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { mapGetters } from "vuex";
import { toast } from "../../main";
import { Select } from "primevue";
import MultiSelect from "primevue/multiselect";
import DashboardSection from "../../components/dashboard/DashboardSection.vue";
import DashboardFilterChips from "../../components/dashboard/DashboardFilterChips.vue";
import ComplianceReviewSection from "../../components/dashboard/ComplianceReviewSection.vue";
import FindingsSection from "../../components/dashboard/FindingsSection.vue";
import KpisSection from "../../components/dashboard/KpisSection.vue";
import ControlsSection from "../../components/dashboard/ControlsSection.vue";
import {
  buildFilterChips,
  clearFilterKey,
  cloneFilters,
  countActiveFilters,
  emptyControlFilters,
  emptyCrFilters,
  emptyFindingsFilters,
  emptyGlobalFilters,
  emptyKpiFilters,
  mergeFilters,
  normalizeFilterPayload,
} from "../../utils/dashboardFilters";
import "../../styles/dashboard.css";

export default defineComponent({
  components: {
    Select,
    MultiSelect,
    DashboardSection,
    DashboardFilterChips,
    ComplianceReviewSection,
    FindingsSection,
    KpisSection,
    ControlsSection,
  },
  data(): any {
    return {
      stickyOffset: 0,
      headerResizeObserver: null,
      globalFilters: emptyGlobalFilters(),
      appliedGlobalFilters: emptyGlobalFilters(),
      crFilters: emptyCrFilters(),
      appliedCrFilters: emptyCrFilters(),
      findingsFilters: emptyFindingsFilters(),
      appliedFindingsFilters: emptyFindingsFilters(),
      kpiFilters: emptyKpiFilters(),
      appliedKpiFilters: emptyKpiFilters(),
      controlFilters: emptyControlFilters(),
      appliedControlFilters: emptyControlFilters(),
      summaryKPI: {},
      crLoading: false,
      findingsLoading: false,
      kpiLoading: false,
      controlLoading: false,
      crTasks: [],
      teamTasks: [],
      overdueAging: [],
      findingChartData: [],
      teamFindings: [],
      capaTasks: [],
      capaTeamTasks: [],
      capaOverdueByTeam: [],
      kpiSeverities: [],
      kpiHealthData: [],
      kpiTeams: [],
      kpiResults: [],
      kpiResultsByTeam: [],
      kpiResultsByLocation: [],
      controlSeverities: [],
      controlHealthData: [],
      controlTeams: [],
      departmentsList: [],
      locations: [],
      controlsList: [],
      optionsActivityArray: [],
      optionsKpiArray: [],
      optionSystemArray: [],
      optionsForFrameworks: [],
      optionsForFindingIds: [],
      optionsProjectArray: [],
      statusCodes: [
        { id: 10401, value: "Open" },
        { id: 10402, value: "In Progress" },
        { id: 10408, value: "Approval" },
        { id: 10419, value: "For Review" },
        { id: 10415, value: "In Review" },
        { id: 10403, value: "Completed" },
        { id: 10406, value: "Partial" },
        { id: 10418, value: "Terminated" },
        { id: 10417, value: "Not Applicable" },
      ],
      yearSeries: [
        { id: 20601, value: "This Year" },
        { id: 20602, value: "Last Year" },
        { id: 20603, value: "Past 6 Months" },
      ],
      kpiYearSeries: [
        { id: 20601, value: "This Year" },
        { id: 20602, value: "Last Year" },
        { id: 20603, value: "Past 6 Months" },
        { id: 20604, value: "Past 3 Months" },
      ],
      sectionLinks: [
        { id: "compliance-review", label: "Control Review" },
        { id: "kpis", label: "KPI Results" },
        { id: "controls", label: "Control Health" },
        { id: "findings", label: "Findings & CAPAs" },
      ],
      activeSection: "compliance-review",
      sectionObserver: null,
      tierTypes: [
        { id: 10107, value: "Activity" },
        { id: 10108, value: "System" },
        { id: 10109, value: "Project" },
      ],
      taskType: [
        { id: 20501, value: "CR Task" },
        { id: 20502, value: "CAPA Task" },
      ],
    };
  },
  computed: {
    ...mapGetters({
      userInfo: "userInfo",
      dbPriority: "dbPriority",
      dbFrequency: "dbFrequency",
      statusInfo: "statusInfo",
    }),
    kpiCards(): { key: string; label: string; display: string; tone: string }[] {
      const kpi = this.summaryKPI || {};
      return [
        { key: "controlsMonitored", label: "Active Controls", display: String(kpi.Controls_Monitored || 0), tone: "neutral" },
        { key: "controlsNotReviewed", label: "Controls Not Measured", display: String(kpi.Controls_Not_Reviewed || 0), tone: "muted" },
        { key: "reviewsPending", label: "Control Reviews Pending", display: String(kpi.Reviews_Pending || 0), tone: "attention" },
        { key: "reviewOverdue", label: "Control Reviews Overdue", display: String(kpi.Review_Overdue || 0), tone: "risk" },
        { key: "kpiPassRate", label: "KPI Pass Rate", display: `${kpi.KPI_Pass_Rate || 0}%`, tone: "positive" },
        { key: "openFindings", label: "Open Findings", display: String(kpi.Open_Findings || 0), tone: "attention" },
        { key: "openCapas", label: "Open CAPAs", display: String(kpi.Open_CAPAs || 0), tone: "attention" },
        { key: "overdueCapas", label: "Overdue CAPAs", display: String(kpi.Overdue_CAPAs || 0), tone: "risk" },
      ];
    },
    globalChips(): { key: string; label: string; value: string; fullValue: string }[] {
      return buildFilterChips(this.appliedGlobalFilters);
    },
    globalFilterCount(): number {
      return countActiveFilters(this.appliedGlobalFilters);
    },
    crChips(): { key: string; label: string; value: string; fullValue: string }[] {
      return buildFilterChips(this.appliedCrFilters, { control: this.controlsList });
    },
    findingsChips(): { key: string; label: string; value: string; fullValue: string }[] {
      return buildFilterChips(this.appliedFindingsFilters);
    },
    kpiChips(): { key: string; label: string; value: string; fullValue: string }[] {
      return buildFilterChips(this.appliedKpiFilters, { kpiId: this.optionsKpiArray });
    },
    controlChips(): { key: string; label: string; value: string; fullValue: string }[] {
      return buildFilterChips(this.appliedControlFilters, { control: this.controlsList });
    },
    crFilterCount(): number {
      return countActiveFilters(this.appliedCrFilters);
    },
    findingsFilterCount(): number {
      return countActiveFilters(this.appliedFindingsFilters);
    },
    kpiFilterCount(): number {
      return countActiveFilters(this.appliedKpiFilters);
    },
    controlFilterCount(): number {
      return countActiveFilters(this.appliedControlFilters);
    },
  },
  mounted() {
    this.fetchDepartmentsList();
    this.getAllBusinessProcesses();
    this.getAllProjects();
    this.getAllSystems();
    this.getAllEntities();
    this.getAllControls();
    this.getAllFrameworks();
    this.getAllFindings();
    this.getAllKpis();
    this.applyGlobalFilters();
    this.$nextTick(() => {
      this.setupStickyHeader();
      this.setupSectionObserver();
    });
  },
  beforeUnmount() {
    this.teardownStickyHeader();
    this.teardownSectionObserver();
  },
  methods: {
    setupSectionObserver() {
      this.teardownSectionObserver();
      const root = (this.$el as HTMLElement)?.closest(".content") || null;
      this.sectionObserver = new IntersectionObserver(
        (entries: IntersectionObserverEntry[]) => {
          const visible = entries
            .filter((entry) => entry.isIntersecting)
            .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
          if (visible?.target?.id) {
            this.activeSection = visible.target.id;
          }
        },
        { root, rootMargin: `-${Math.max(this.stickyOffset, 72)}px 0px -45% 0px`, threshold: [0.15, 0.35, 0.6] }
      );
      this.sectionLinks.forEach((section: { id: string }) => {
        const element = document.getElementById(section.id);
        if (element) {
          this.sectionObserver.observe(element);
        }
      });
    },
    teardownSectionObserver() {
      if (this.sectionObserver) {
        this.sectionObserver.disconnect();
        this.sectionObserver = null;
      }
    },
    setupStickyHeader() {
      this.teardownStickyHeader();
      const header = this.$refs.stickyHeader as HTMLElement | undefined;
      if (header && typeof ResizeObserver !== "undefined") {
        this.headerResizeObserver = new ResizeObserver(() => this.updateStickyOffset());
        this.headerResizeObserver.observe(header);
      }
      this.updateStickyOffset();
    },
    teardownStickyHeader() {
      if (this.headerResizeObserver) {
        this.headerResizeObserver.disconnect();
        this.headerResizeObserver = null;
      }
    },
    updateStickyOffset() {
      const header = this.$refs.stickyHeader as HTMLElement | undefined;
      const offset = (header?.offsetHeight || 0) + 8;
      if (offset === this.stickyOffset) {
        return;
      }
      this.stickyOffset = offset;
      (this.$el as HTMLElement)?.style?.setProperty("--dash-sticky-offset", `${offset}px`);
      this.setupSectionObserver();
    },
    scrollToSection(sectionId: string) {
      const element = document.getElementById(sectionId);
      if (!element) {
        return;
      }
      this.activeSection = sectionId;
      const scroller = ((this.$el as HTMLElement)?.closest(".content") || null) as HTMLElement | null;
      const headerHeight = (this.$refs.stickyHeader as HTMLElement | undefined)?.offsetHeight || this.stickyOffset;
      if (scroller) {
        const nextTop = scroller.scrollTop + element.getBoundingClientRect().top - scroller.getBoundingClientRect().top - headerHeight - 8;
        scroller.scrollTo({ top: Math.max(0, nextTop), behavior: "smooth" });
        return;
      }
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    crPayload() {
      return { filters: normalizeFilterPayload(mergeFilters(this.appliedGlobalFilters, this.appliedCrFilters)) };
    },
    findingsChartsPayload() {
      return { filters: normalizeFilterPayload(mergeFilters(this.appliedGlobalFilters, this.appliedFindingsFilters)) };
    },
    capaChartsPayload() {
      return { filters: normalizeFilterPayload(mergeFilters(this.appliedGlobalFilters, this.appliedFindingsFilters)) };
    },
    kpiPayload() {
      const merged = mergeFilters(
        {
          timePeriod: this.appliedGlobalFilters.timePeriod,
          locations: this.appliedGlobalFilters.locations,
          teams: this.appliedGlobalFilters.departments,
          departments: this.appliedGlobalFilters.departments,
        },
        this.appliedKpiFilters
      );
      return { kpiFilters: normalizeFilterPayload(merged) };
    },
    controlPayload() {
      const merged = mergeFilters(
        {
          timePeriod: this.appliedGlobalFilters.timePeriod,
          locations: this.appliedGlobalFilters.locations,
          teams: this.appliedGlobalFilters.departments,
          departments: this.appliedGlobalFilters.departments,
        },
        this.appliedControlFilters
      );
      return { controlFilters: normalizeFilterPayload(merged) };
    },
    applyGlobalFilters() {
      this.appliedGlobalFilters = cloneFilters(this.globalFilters);
      this.showGlobalFilters = false;
      this.fetchSummaryData();
      this.fetchComplianceReview();
      this.fetchFindings();
      this.fetchKpiData();
      this.fetchControlData();
    },
    resetGlobalFilters() {
      this.globalFilters = emptyGlobalFilters();
      this.appliedGlobalFilters = emptyGlobalFilters();
      this.showGlobalFilters = false;
      this.fetchSummaryData();
      this.fetchComplianceReview();
      this.fetchFindings();
      this.fetchKpiData();
      this.fetchControlData();
    },
    cancelGlobalFilters() {
      this.globalFilters = cloneFilters(this.appliedGlobalFilters);
      this.showGlobalFilters = false;
    },
    toggleGlobalFilters() {
      if (this.showGlobalFilters) {
        this.cancelGlobalFilters();
        return;
      }
      this.globalFilters = cloneFilters(this.appliedGlobalFilters);
      this.showGlobalFilters = true;
    },
    removeGlobalFilter(key: string) {
      this.appliedGlobalFilters = clearFilterKey(this.appliedGlobalFilters, key);
      this.globalFilters = clearFilterKey(this.globalFilters, key);
      this.fetchSummaryData();
      this.fetchComplianceReview();
      this.fetchFindings();
      this.fetchKpiData();
      this.fetchControlData();
    },
    openCrFilters() {
      this.crFilters = cloneFilters(this.appliedCrFilters);
    },
    applyCrFilters() {
      this.appliedCrFilters = cloneFilters(this.crFilters);
      this.fetchComplianceReview();
    },
    resetCrFilters() {
      this.crFilters = emptyCrFilters();
      this.appliedCrFilters = emptyCrFilters();
      this.fetchComplianceReview();
    },
    cancelCrFilters() {
      this.crFilters = cloneFilters(this.appliedCrFilters);
    },
    removeCrFilter(key: string) {
      this.appliedCrFilters = clearFilterKey(this.appliedCrFilters, key);
      this.crFilters = clearFilterKey(this.crFilters, key);
      if (key === "tier") {
        this.appliedCrFilters = {
          ...this.appliedCrFilters,
          activity: [],
          system: [],
          project: [],
        };
        this.crFilters = {
          ...this.crFilters,
          activity: [],
          system: [],
          project: [],
        };
      }
      this.fetchComplianceReview();
    },
    openFindingsFilters() {
      this.findingsFilters = cloneFilters(this.appliedFindingsFilters);
    },
    applyFindingsFilters() {
      this.appliedFindingsFilters = cloneFilters(this.findingsFilters);
      this.fetchFindings();
    },
    resetFindingsFilters() {
      this.findingsFilters = emptyFindingsFilters();
      this.appliedFindingsFilters = emptyFindingsFilters();
      this.fetchFindings();
    },
    cancelFindingsFilters() {
      this.findingsFilters = cloneFilters(this.appliedFindingsFilters);
    },
    removeFindingsFilter(key: string) {
      this.appliedFindingsFilters = clearFilterKey(this.appliedFindingsFilters, key);
      this.findingsFilters = clearFilterKey(this.findingsFilters, key);
      this.fetchFindings();
    },
    openKpiFilters() {
      this.kpiFilters = cloneFilters(this.appliedKpiFilters);
    },
    applyKpiFilters() {
      this.appliedKpiFilters = cloneFilters(this.kpiFilters);
      this.fetchKpiData();
    },
    resetKpiFilters() {
      this.kpiFilters = emptyKpiFilters();
      this.appliedKpiFilters = emptyKpiFilters();
      this.fetchKpiData();
    },
    cancelKpiFilters() {
      this.kpiFilters = cloneFilters(this.appliedKpiFilters);
    },
    removeKpiFilter(key: string) {
      this.appliedKpiFilters = clearFilterKey(this.appliedKpiFilters, key);
      this.kpiFilters = clearFilterKey(this.kpiFilters, key);
      this.fetchKpiData();
    },
    openControlFilters() {
      this.controlFilters = cloneFilters(this.appliedControlFilters);
    },
    applyControlFilters() {
      this.appliedControlFilters = cloneFilters(this.controlFilters);
      this.fetchControlData();
    },
    resetControlFilters() {
      this.controlFilters = emptyControlFilters();
      this.appliedControlFilters = emptyControlFilters();
      this.fetchControlData();
    },
    cancelControlFilters() {
      this.controlFilters = cloneFilters(this.appliedControlFilters);
    },
    removeControlFilter(key: string) {
      this.appliedControlFilters = clearFilterKey(this.appliedControlFilters, key);
      this.controlFilters = clearFilterKey(this.controlFilters, key);
      this.fetchControlData();
    },
    onCategoryChange(event: any) {
      if (event == 10107) {
        this.crFilters.system = [];
        this.crFilters.project = [];
      }
      if (event == 10108) {
        this.crFilters.activity = [];
        this.crFilters.project = [];
      }
      if (event == 10109) {
        this.crFilters.activity = [];
        this.crFilters.system = [];
      }
    },
    async fetchDepartmentsList() {
      try {
        const res = await this.$http.get(`${process.env.VUE_APP_ORG_API}/departments/get`);
        res.data.map((obj: any) => {
          this.departmentsList.push({ value: obj._id, label: obj.displayName });
        });
      } catch (error) {
        toast.error(`Departments fetch failed: ${error}`);
      }
    },
    async getAllBusinessProcesses() {
      const result = await this.$http.get(`${process.env.VUE_APP_ORG_API}/api/org/getall/${20006}`);
      const allProcess = result.data || [];
      allProcess.forEach((item: any) => {
        this.optionsActivityArray.push({
          label: item.businessProcessName,
          value: item._id,
          departments: item.department,
        });
      });
    },
    async getAllKpis() {
      const result = await this.$http.get(`${process.env.VUE_APP_CONTROLS_API}/api/org/kpis/getall`);
      const allKpis = (result.data || []).flatMap((item: any) => item.kpis);
      allKpis.forEach((item: any) => {
        this.optionsKpiArray.push({ label: item.description, value: item._id });
      });
    },
    async getAllProjects() {
      const result = await this.$http.get(`${process.env.VUE_APP_ORG_API}/api/org/projects/getAll`);
      (result.data || []).forEach((item: any) => {
        this.optionsProjectArray.push({
          label: item.projectName,
          value: item._id,
          departments: item.department,
        });
      });
    },
    async getAllSystems() {
      const result = await this.$http.get(`${process.env.VUE_APP_ORG_API}/api/org/getall/${20005}`);
      (result.data || []).forEach((item: any) => {
        this.optionSystemArray.push({ label: item.systemName, value: item._id });
      });
    },
    async getAllFrameworks() {
      const result = await this.$http.get(`${process.env.VUE_APP_CONTROLS_API}/api/tenant/org/controls/get`);
      (result.data || []).forEach((item: any) => {
        if (item.isActive && item.frameWorkName != "InternalControls") {
          this.optionsForFrameworks.push({ label: item.frameWorkName, value: item._id });
        }
      });
    },
    async getAllFindings() {
      const result = await this.$http.get(`${process.env.VUE_APP_MONITOR_API}/api/org/findings/getall`);
      (result.data || []).forEach((item: any) => {
        this.optionsForFindingIds.push({ label: item.findingId, value: item._id });
      });
    },
    async getAllEntities() {
      try {
        const res = await this.$http.get(`${process.env.VUE_APP_ORG_API}/api/org/businessEntities/getall`);
        (res.data || []).forEach((obj: any) => {
          this.locations.push({ value: obj._id, label: obj.entityDisplayName });
        });
      } catch (error) {
        toast.error(`Entities fetch failed: ${error}`);
      }
    },
    async getAllControls() {
      try {
        const res = await this.$http.get(`${process.env.VUE_APP_CONTROLS_API}/api/org/controls/active/getall`);
        (res.data || []).forEach((control: any) => {
          this.controlsList.push({ label: control.clientUniqueId, value: control._id });
        });
      } catch (error: any) {
        toast.error(`Controls fetch failed: ${error}`);
      }
    },
    async fetchSummaryData() {
      try {
        const res = await this.$http.post(`${process.env.VUE_APP_ANALYTICS_API}/monitor/summary`, {
          filters: normalizeFilterPayload(this.appliedGlobalFilters),
        });
        this.summaryKPI = res?.data?.KPI || {};
      } catch (error) {
        // Keep the KPI strip visible with zeros when summary is unavailable.
        this.summaryKPI = {};
      }
    },
    async fetchComplianceReview() {
      this.crLoading = true;
      try {
        const res = await this.$http.post(
          `${process.env.VUE_APP_ANALYTICS_API}/monitor/dashboard`,
          this.crPayload()
        );
        this.crTasks = res?.data?.CR_Tasks || [];
        this.teamTasks = res?.data?.Team_Tasks || [];
        this.overdueAging = res?.data?.Overdue_Aging || [];
      } catch (error) {
        this.crTasks = [];
        this.teamTasks = [];
        this.overdueAging = [];
        toast.error(`Compliance Review fetch failed: ${error}`);
      } finally {
        this.crLoading = false;
      }
    },
    async fetchFindings() {
      this.findingsLoading = true;
      try {
        const [findingsRes, capaRes] = await Promise.all([
          this.$http.post(`${process.env.VUE_APP_ANALYTICS_API}/monitor/findings/dashboard`, this.findingsChartsPayload()),
          this.$http.post(`${process.env.VUE_APP_ANALYTICS_API}/monitor/dashboard`, this.capaChartsPayload()),
        ]);
        this.findingChartData = findingsRes?.data?.Findings || [];
        this.teamFindings = findingsRes?.data?.Team_Findings || [];
        this.capaTasks = capaRes?.data?.CAPA_Tasks || [];
        this.capaTeamTasks = capaRes?.data?.CAPA_Team_Tasks || [];
        this.capaOverdueByTeam = capaRes?.data?.CAPA_Overdue_By_Team || [];
      } catch (error) {
        this.findingChartData = [];
        this.teamFindings = [];
        this.capaTasks = [];
        this.capaTeamTasks = [];
        this.capaOverdueByTeam = [];
        toast.error(`Findings fetch failed: ${error}`);
      } finally {
        this.findingsLoading = false;
      }
    },
    async fetchKpiData() {
      this.kpiLoading = true;
      try {
        const kpiResult = await this.$http.post(
          `${process.env.VUE_APP_ANALYTICS_API}/monitor/kpis/dashboard`,
          this.kpiPayload()
        );
        this.kpiSeverities = kpiResult?.data?.allKpiRiskSeverities || [];
        this.kpiHealthData = kpiResult?.data?.KPI || [];
        this.kpiTeams = kpiResult?.data?.KPI_Teams || [];
        this.kpiResults = kpiResult?.data?.KPI_Results || [];
        this.kpiResultsByTeam = kpiResult?.data?.KPI_Results_By_Team || [];
        this.kpiResultsByLocation = kpiResult?.data?.KPI_Results_By_Location || [];
      } catch (error) {
        this.kpiSeverities = [];
        this.kpiHealthData = [];
        this.kpiTeams = [];
        this.kpiResults = [];
        this.kpiResultsByTeam = [];
        this.kpiResultsByLocation = [];
        toast.error(`KPI fetch failed: ${error}`);
      } finally {
        this.kpiLoading = false;
      }
    },
    async fetchControlData() {
      this.controlLoading = true;
      try {
        const controlResult = await this.$http.post(
          `${process.env.VUE_APP_ANALYTICS_API}/monitor/controls/dashboard`,
          this.controlPayload()
        );
        this.controlSeverities = controlResult?.data?.controlRiskSeverity || [];
        this.controlHealthData = controlResult?.data?.Controls || [];
        this.controlTeams = controlResult?.data?.Control_Teams || [];
      } catch (error) {
        this.controlSeverities = [];
        this.controlHealthData = [];
        this.controlTeams = [];
        toast.error(`Controls fetch failed: ${error}`);
      } finally {
        this.controlLoading = false;
      }
    },
    kpiQuery(key: string) {
      const query: Record<string, string> = { kpi: key };
      if (this.appliedGlobalFilters.timePeriod) {
        query.timePeriod = String(this.appliedGlobalFilters.timePeriod);
      }
      return query;
    },
    kpiRoute(key: string) {
      const routes: Record<string, string> = {
        controlsMonitored: "Active-Controls",
        controlsNotReviewed: "Active-Controls",
        reviewsPending: "complianceReview",
        reviewOverdue: "complianceReview",
        kpiPassRate: "View-KPIS",
        openFindings: "complianceFindings",
        openCapas: "findingTasks",
        overdueCapas: "findingTasks",
      };
      return { name: routes[key] || "dashboard", query: this.kpiQuery(key) };
    },
    markKpiRoute(key: string) {
      const route = this.kpiRoute(key);
      this.$store.dispatch("selectedRoute", route.name);
    },
  },
});
</script>
